import { z } from 'zod';
import { KAMPAGNE_FORMATE, type KampagneTyp, type WkzArt, type WkzEinheit } from './kampagnen';
import type { GuthabenStand } from './werbung';
import type { WerbemittelArt, WerbemittelFreigabeDurch, WerbemittelKurz, WerbemittelStatus } from './werbemittel';

/**
 * Retail Media RM-9: WKZ-Buchung (Vorgabe docs/retail-media-formate.md, „WKZ-Buchung“).
 *
 * Festpreis statt CPC: Einheit „tag“ bucht einzelne Tage, „woche“ immer ganze Kalenderwochen Mo–So, „stueck“ eine
 * Menge (z. B. zwei Newsletter) im gewählten Zeitraum. Preis = Einheiten × wkz_preis_cent. Frühester Start ist
 * heute + Vorlauf der Fläche (Europe/Berlin, mindestens morgen). Belegt wird je Tag ein Platz (wkz_belegung,
 * eindeutig je Platzierung, Tag und Platz) – Kapazität 1 ist exklusiv.
 *
 * Ablauf (Migration 0590): Der Werbetreibende fragt an – ohne Werbemittel. Der Marktplatz bestätigt oder lehnt ab.
 * Das Werbemittel erstellt je Buchung der Werbetreibende oder der Marktplatz; die jeweils andere Seite gibt frei.
 * Ist die Buchung bestätigt und das Werbemittel freigegeben, stellt der Marktplatz sie live – erst dann wird
 * ausgespielt. Den Stand für Anzeige und Aufgaben liefert wkzPhase().
 *
 * Datumsangaben sind Kalendertage JJJJ-MM-TT (ganztägig Berlin). Gerechnet wird ohne Zeitzone über UTC-Mitternacht,
 * damit Sommer-/Winterzeit keine Tage verschiebt.
 */

/** Gespeicherter Status */
export const WKZ_STATUS = { angefragt: 'Angefragt', bestaetigt: 'Bestätigt', live: 'Live', abgelehnt: 'Abgelehnt', storniert: 'Storniert' } as const;
export type WkzStatus = keyof typeof WKZ_STATUS;
const STATUS = ['angefragt', 'bestaetigt', 'live', 'abgelehnt', 'storniert'] as const;
/** Diese Buchungen belegen ihre Tage */
export const WKZ_STATUS_BELEGT: readonly WkzStatus[] = ['angefragt', 'bestaetigt', 'live'];
/** Diese Buchungen zählen als WKZ-Umsatz (bestätigt) */
export const WKZ_STATUS_UMSATZ: readonly WkzStatus[] = ['bestaetigt', 'live'];

/** Stand der Buchung aus Status, Werbemittel und Zeitraum – für Anzeige, Filter und Aufgaben */
export const WKZ_PHASE = {
  angefragt: 'Angefragt',
  werbemittel_offen: 'Werbemittel offen',
  in_freigabe: 'In Freigabe',
  bereit: 'Bereit',
  live: 'Live',
  beendet: 'Beendet',
  abgelaufen: 'Abgelaufen',
  abgelehnt: 'Abgelehnt',
  storniert: 'Storniert',
} as const;
export type WkzPhase = keyof typeof WKZ_PHASE;

/** Wer das Werbemittel einer Buchung erstellt – die andere Seite gibt frei */
export const WKZ_WERBEMITTEL_VON = { werbetreibender: 'Werbetreibender', marktplatz: 'Marktplatz' } as const;
export type WkzWerbemittelVon = keyof typeof WKZ_WERBEMITTEL_VON;

export interface WkzPhaseEingabe {
  status: WkzStatus;
  bis: string;
  werbemittelVon: WkzWerbemittelVon;
  werbemittelStatus: WerbemittelStatus | null;
}

/**
 * Stand einer Buchung. Live mit Werbemittel, das nicht (mehr) freigegeben ist – z. B. nach einer Änderung – zählt
 * als „in Freigabe“: Ausgespielt wird erst wieder nach der Freigabe.
 */
export function wkzPhase(b: WkzPhaseEingabe, heute: string): WkzPhase {
  if (b.status === 'abgelehnt' || b.status === 'storniert') return b.status;
  if (b.status === 'angefragt') return heute > b.bis ? 'abgelaufen' : 'angefragt';
  const wm = b.werbemittelStatus;
  if (b.status === 'live') {
    if (heute > b.bis) return 'beendet';
    return wm === 'freigegeben' ? 'live' : wm === 'pruefung' ? 'in_freigabe' : 'werbemittel_offen';
  }
  if (heute > b.bis) return 'abgelaufen';
  return wm === 'freigegeben' ? 'bereit' : wm === 'pruefung' ? 'in_freigabe' : 'werbemittel_offen';
}

/** Nächster Schritt: wer jetzt dran ist und was zu tun ist (null: niemand) */
export function wkzNaechsterSchritt(b: WkzPhaseEingabe, phase: WkzPhase): { wer: WkzWerbemittelVon; text: string } | null {
  const ersteller = b.werbemittelVon;
  const pruefer: WkzWerbemittelVon = ersteller === 'marktplatz' ? 'werbetreibender' : 'marktplatz';
  switch (phase) {
    case 'angefragt':
      return { wer: 'marktplatz', text: 'Anfrage bestätigen oder ablehnen' };
    case 'werbemittel_offen':
      return {
        wer: ersteller,
        text: b.werbemittelStatus === 'abgelehnt' ? 'Werbemittel überarbeiten'
          : ersteller === 'marktplatz' ? 'Werbemittel gestalten und vorlegen' : 'Werbemittel hochladen',
      };
    case 'in_freigabe':
      return { wer: pruefer, text: pruefer === 'marktplatz' ? 'Werbemittel prüfen' : 'Werbemittel freigeben' };
    case 'bereit':
      return { wer: 'marktplatz', text: 'Buchung live stellen' };
    default:
      return null;
  }
}

export const WKZ_ABRECHNUNG = { guthaben: 'Guthaben', rechnung: 'Rechnung' } as const;
export type WkzAbrechnung = keyof typeof WKZ_ABRECHNUNG;

export const WKZ_ANGELEGT_VON = { werbetreibender: 'Werbetreibender', marktplatz: 'Marktplatz' } as const;
export type WkzAngelegtVon = keyof typeof WKZ_ANGELEGT_VON;

const WKZ_GRENZEN_MENGE = 100;

export const WKZ_GRENZEN = {
  /** Kalender: Standardzeitraum ab heute */
  kalenderWochen: 12,
  /** Kalender: längster abfragbarer Zeitraum */
  kalenderTageMax: 371,
  /** längste Buchung */
  buchungTageMax: 182,
  /** spätester Start ab heute */
  vorlaufTageMax: 365,
  grund: 500,
  preisMaxCent: 100_000_000,
  /** höchste Menge bei Preis je Stück */
  mengeMax: WKZ_GRENZEN_MENGE,
} as const;

/* ------------------------------------------------------------------ Datumsrechnung */

const DATUM = /^\d{4}-\d{2}-\d{2}$/;
const alsUtc = (d: string) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
const ausUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const TAG_MS = 86_400_000;

/** Gültiges Kalenderdatum JJJJ-MM-TT (kein 31. Februar) */
export const wkzDatumGueltig = (d: string) => DATUM.test(d) && ausUtc(alsUtc(d)) === d;
export const wkzDatumPlus = (d: string, tage: number) => ausUtc(alsUtc(d) + tage * TAG_MS);
/** Tage von a bis b, beide eingeschlossen (b < a → 0) */
export const wkzTageZwischen = (a: string, b: string) => Math.max(0, Math.round((alsUtc(b) - alsUtc(a)) / TAG_MS) + 1);
/** 1 = Montag … 7 = Sonntag */
export const wkzWochentag = (d: string) => ((new Date(alsUtc(d)).getUTCDay() + 6) % 7) + 1;
/** Montag der Kalenderwoche */
export const wkzWocheBeginn = (d: string) => wkzDatumPlus(d, 1 - wkzWochentag(d));
/** Kalenderwoche nach ISO 8601 */
export function wkzKalenderwoche(d: string): number {
  const donnerstag = wkzDatumPlus(d, 4 - wkzWochentag(d));
  const jahresbeginn = `${donnerstag.slice(0, 4)}-01-01`;
  return Math.floor((wkzTageZwischen(jahresbeginn, donnerstag) - 1) / 7) + 1;
}
/** Alle Tage von a bis b */
export function wkzTageListe(a: string, b: string): string[] {
  const n = wkzTageZwischen(a, b);
  return Array.from({ length: n }, (_, i) => wkzDatumPlus(a, i));
}
/** Heute in Europe/Berlin (JJJJ-MM-TT) */
export const wkzHeute = (jetzt = new Date()) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(jetzt);
/** Frühester Start einer Buchung: heute + Vorlauf (Berlin), mindestens morgen */
export const wkzFruehesterStart = (heute: string, vorlaufTage = 1) => wkzDatumPlus(heute, Math.max(1, vorlaufTage));
/** Frühester Start bei Einheit Woche: der nächste Montag ab dem frühesten Tag */
export function wkzFruehesterStartFuer(einheit: WkzEinheit, heute: string, vorlaufTage = 1): string {
  const erster = wkzFruehesterStart(heute, vorlaufTage);
  return einheit === 'woche' && wkzWochentag(erster) !== 1 ? wkzDatumPlus(wkzWocheBeginn(erster), 7) : erster;
}

/* ------------------------------------------------------------------ Zeitraum und Preis */

/** Zeitraum für die Einheit prüfen – null, wenn er passt, sonst der Grund (für Formular und Backend) */
export function wkzZeitraumFehler(einheit: WkzEinheit, von: string, bis: string): string | null {
  if (!wkzDatumGueltig(von) || !wkzDatumGueltig(bis)) return 'Bitte gültige Daten angeben.';
  if (bis < von) return 'Das Ende liegt vor dem Start.';
  if (wkzTageZwischen(von, bis) > WKZ_GRENZEN.buchungTageMax) return `Höchstens ${WKZ_GRENZEN.buchungTageMax} Tage am Stück.`;
  if (einheit === 'woche' && (wkzWochentag(von) !== 1 || wkzWochentag(bis) !== 7)) return 'Gebucht werden ganze Kalenderwochen von Montag bis Sonntag.';
  return null;
}

/** Anzahl der Einheiten (Tage, Wochen bzw. Stück) eines gültigen Zeitraums */
export const wkzEinheiten = (einheit: WkzEinheit, von: string, bis: string, menge = 1) =>
  einheit === 'stueck' ? menge : einheit === 'woche' ? Math.floor(wkzTageZwischen(von, bis) / 7) : wkzTageZwischen(von, bis);

/** Listenpreis: Einheiten × Preis je Einheit */
export const wkzPreis = (einheit: WkzEinheit, preisCent: number, von: string, bis: string, menge = 1) => wkzEinheiten(einheit, von, bis, menge) * preisCent;

/**
 * Anteilige Erstattung beim Stornieren: der Preis der noch nicht begonnenen Tage (ab morgen bzw. ab Start).
 * Vor dem Start der volle Preis, nach dem Ende nichts. Abgerundet auf ganze Cent.
 */
export function wkzErstattung(preisCent: number, von: string, bis: string, heute: string): number {
  if (heute < von) return preisCent;
  if (heute >= bis) return 0;
  return Math.floor((preisCent * wkzTageZwischen(wkzDatumPlus(heute, 1), bis)) / wkzTageZwischen(von, bis));
}

/** Werbemittel-Arten, die zu den Formaten einer Platzierung passen (Startseite: display; sonstige Leistung: display) */
export function wkzWerbemittelArten(formate: readonly KampagneTyp[], art: WkzArt = 'shop'): WerbemittelArt[] {
  if (art === 'sonstige') return ['display'];
  const arten = formate.map((f) => KAMPAGNE_FORMATE[f]?.werbemittel).filter((a): a is WerbemittelArt => !!a);
  return [...new Set(arten)];
}

/* ------------------------------------------------------------------ Eingaben */

const Datum = z.string().refine(wkzDatumGueltig, 'Bitte Datum angeben');
const Id = z.string().uuid();

export const WkzKalenderAbfrage = z.object({
  /** Standard: heute */
  von: Datum.optional(),
  /** Standard: von + 12 Wochen − 1 Tag */
  bis: Datum.optional(),
});
export type WkzKalenderAbfrageEingabe = z.infer<typeof WkzKalenderAbfrage>;

export const WkzVerfuegbarkeitAbfrage = WkzKalenderAbfrage.extend({ platzierung: Id });

/**
 * Werbetreibender (Portal): Anfrage aus dem Kalender, Abrechnung immer über das Guthaben. Ein Werbemittel ist
 * optional – es lässt sich später zuordnen, oder der Marktplatz gestaltet es (werbemittelVon „marktplatz“).
 */
export const WkzBuchenSchema = z.object({
  platzierungId: Id,
  von: Datum,
  bis: Datum,
  /** nur bei Preis je Stück */
  menge: z.number().int('Bitte ganze Zahl angeben').min(1, 'Mindestens 1').max(WKZ_GRENZEN_MENGE, `Höchstens ${WKZ_GRENZEN_MENGE}`).default(1),
  werbemittelVon: z.enum(['werbetreibender', 'marktplatz']).default('werbetreibender'),
  werbemittelId: Id.nullable().default(null),
  /** Wünsche an das Werbemittel, wenn der Marktplatz gestaltet */
  briefing: z.string().trim().max(2000, 'Höchstens 2000 Zeichen').default(''),
});
export type WkzBuchen = z.infer<typeof WkzBuchenSchema>;

/** Marktplatz (Konto): für ein Werbekonto buchen – abweichender Preis (auch 0) und Abrechnung frei, direkt bestätigt */
export const KontoWkzBuchenSchema = WkzBuchenSchema.extend({
  werbekontoId: Id,
  /** null: Listenpreis (Einheiten × Preis je Einheit) */
  preisCent: z.number().int('Bitte Betrag in Cent angeben').min(0, 'Nicht negativ').max(WKZ_GRENZEN.preisMaxCent, 'Preis zu hoch').nullable().default(null),
  abrechnung: z.enum(['guthaben', 'rechnung']).default('guthaben'),
});
export type KontoWkzBuchen = z.infer<typeof KontoWkzBuchenSchema>;

/** Werbetreibender ordnet der Buchung ein eigenes Werbemittel zu (Entwürfe werden dabei eingereicht) */
export const WkzWerbemittelZuordnenSchema = z.object({ werbemittelId: Id });

/** Flächen des Marktplatzes – anlegen und ändern (Schlüssel und Art nur beim Anlegen) */
export const WkzFlaecheSchema = z.object({
  name: z.string().trim().min(2, 'Bitte Namen angeben').max(80, 'Höchstens 80 Zeichen'),
  beschreibung: z.string().trim().max(1000, 'Höchstens 1000 Zeichen').default(''),
  einheit: z.enum(['tag', 'woche', 'stueck']),
  preisCent: z.number().int('Bitte Betrag in Cent angeben').min(0, 'Nicht negativ').max(WKZ_GRENZEN.preisMaxCent, 'Preis zu hoch'),
  kapazitaet: z.number().int('Bitte ganze Zahl angeben').min(1, 'Mindestens 1').max(20, 'Höchstens 20'),
  vorlaufTage: z.number().int('Bitte ganze Zahl angeben').min(1, 'Mindestens 1 Tag').max(90, 'Höchstens 90 Tage'),
  masse: z.string().trim().max(200, 'Höchstens 200 Zeichen').default(''),
  hinweise: z.string().trim().max(1000, 'Höchstens 1000 Zeichen').default(''),
  aktiv: z.boolean(),
  /** nur im Shop: an freien Tagen CPC-Auktion */
  auktionOhneBuchung: z.boolean().default(false),
});
export type WkzFlaecheEingabe = z.infer<typeof WkzFlaecheSchema>;
export const WkzFlaecheAnlegenSchema = WkzFlaecheSchema.extend({
  art: z.enum(['shop', 'sonstige']),
  /** im Shop: Platzierungsschlüssel für das SDK; sonstige: leer (wird vergeben) */
  schluessel: z.string().trim().regex(/^[a-z][a-z0-9_]{1,39}$/, 'Kleinbuchstaben, Ziffern und _ (2–40 Zeichen)').or(z.literal('')).default(''),
}).superRefine((e, ctx) => {
  if (e.art === 'shop' && !e.schluessel) ctx.addIssue({ code: 'custom', path: ['schluessel'], message: 'Bitte Schlüssel für das Shop-SDK angeben' });
});
export type WkzFlaecheAnlegen = z.infer<typeof WkzFlaecheAnlegenSchema>;

export const WkzAblehnenSchema = z.object({
  grund: z.string().trim().min(3, 'Bitte Grund angeben').max(WKZ_GRENZEN.grund, `Höchstens ${WKZ_GRENZEN.grund} Zeichen`),
});
export type WkzAblehnen = z.infer<typeof WkzAblehnenSchema>;

export const WkzStornierenSchema = z.object({
  grund: z.string().trim().max(WKZ_GRENZEN.grund, `Höchstens ${WKZ_GRENZEN.grund} Zeichen`).default(''),
});
export type WkzStornieren = z.infer<typeof WkzStornierenSchema>;

export const WkzBuchungenAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  status: z.enum(STATUS).optional(),
  /** nur Buchungen, bei denen diese Seite am Zug ist */
  offen: z.enum(['werbetreibender', 'marktplatz']).optional(),
  platzierung: Id.optional(),
  werbekonto: Id.optional(),
  /** Zeitraum: Buchungen, die ihn berühren */
  von: Datum.optional(),
  bis: Datum.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type WkzBuchungenAbfrageEingabe = z.infer<typeof WkzBuchungenAbfrage>;

/* ------------------------------------------------------------------ Antworten */

/** Eine per WKZ buchbare Fläche (Platzierung mit wkz) */
export interface WkzPlatzierung {
  id: string;
  schluessel: string;
  name: string;
  art: WkzArt;
  beschreibung: string;
  masse: string;
  hinweise: string;
  aktiv: boolean;
  preisCent: number;
  einheit: WkzEinheit;
  kapazitaet: number;
  vorlaufTage: number;
  auktionOhneBuchung: boolean;
  formate: KampagneTyp[];
  /** passende Werbemittel (Startseite und sonstige Leistung: display) */
  werbemittelArten: WerbemittelArt[];
  /** eine der drei Standardflächen der Startseite */
  standard: boolean;
  /** aktiv, Preis > 0 und ein passendes Werbemittel – sonst im Portal nicht buchbar */
  buchbar: boolean;
}

export interface WkzTag {
  tag: string;
  /** belegte Plätze (angefragte und bestätigte Buchungen) */
  belegt: number;
  frei: number;
  /** davon eigene Buchungen (nur Portal) */
  eigen: number;
}

export interface WkzVerfuegbarkeit {
  platzierung: WkzPlatzierung;
  von: string;
  bis: string;
  heute: string;
  fruehesterStart: string;
  tage: WkzTag[];
}

export interface WkzBuchung {
  id: string;
  platzierungId: string;
  platzierung: string;
  schluessel: string;
  art: WkzArt;
  /** Vorgaben der Fläche für das Werbemittel */
  masse: string;
  hinweise: string;
  werbekontoId: string;
  werbekonto: string;
  /** null: noch keins zugeordnet */
  werbemittel: (WerbemittelKurz & { ablehnungsgrund: string; freigabeDurch: WerbemittelFreigabeDurch }) | null;
  werbemittelVon: WkzWerbemittelVon;
  briefing: string;
  von: string;
  bis: string;
  einheit: WkzEinheit;
  einheiten: number;
  menge: number;
  phase: WkzPhase;
  /** wer als Nächstes dran ist (null: niemand) */
  naechsterSchritt: { wer: WkzWerbemittelVon; text: string } | null;
  liveAm: string | null;
  preisCent: number;
  abrechnung: WkzAbrechnung;
  status: WkzStatus;
  grund: string;
  angelegtVon: WkzAngelegtVon;
  erstelltAm: string;
  entschiedenAm: string | null;
  /** Werbetreibender: bis zum Vortag des Starts; Marktplatz: solange nicht beendet */
  stornierbar: boolean;
  /** Erstattung beim Stornieren heute (bei Abrechnung guthaben, sonst 0) */
  erstattungCent: number;
  /** Ausspielung (gezählt, nie berechnet) */
  impressionen: number;
  klicks: number;
  /** Rechnung und ggf. Storno-Rechnung (nur bei Abrechnung „rechnung“ und Preis über 0) */
  rechnungen: WkzRechnung[];
}

/* ------------------------------------------------------------------ Rechnung bei Abrechnung „rechnung“ */

export const WKZ_RECHNUNG_ARTEN = { rechnung: 'Rechnung', storno: 'Storno-Rechnung' } as const;
export type WkzRechnungArt = keyof typeof WKZ_RECHNUNG_ARTEN;
/** Nummernkreis je Marktplatz und Jahr, z. B. WK-2026-00001 */
export const WKZ_RECHNUNG_PRAEFIX: Record<WkzRechnungArt, string> = { rechnung: 'WK', storno: 'WS' };

export interface WkzRechnung {
  id: string;
  art: WkzRechnungArt;
  nr: string;
  datum: string;
  /** WKZ-Preis ist netto; Storno negativ */
  nettoCent: number;
  ustCent: number;
  bruttoCent: number;
}

/** Festgeschriebener Inhalt (beim Erstellen gespeichert, das PDF entsteht daraus) */
export interface WkzRechnungDaten {
  art: WkzRechnungArt;
  nr: string;
  datum: string;
  /** bei Storno: Nummer der ursprünglichen Rechnung */
  bezug: string | null;
  absender: { name: string; firma: string; strasse: string; ort: string; land: string; ustId: string; registergericht: string; geschaeftsfuehrung: string; email: string };
  empfaenger: { firma: string; strasse: string; ort: string; land: string; ustId: string };
  /** werbemittel: leer, solange keins zugeordnet ist */
  position: { platzierung: string; von: string; bis: string; einheiten: number; einheit: WkzEinheit; werbemittel: string };
  /** Leistungszeitraum einer Storno-Rechnung: die erstatteten Tage */
  zeitraum: [string, string];
  nettoCent: number;
  ustSatz: number;
  ustCent: number;
  bruttoCent: number;
}

export interface WkzBuchungSeite {
  eintraege: WkzBuchung[];
  weiter: string | null;
}

/** Portal → WKZ buchen: Flächen, passende eigene Werbemittel (freigegeben, in Prüfung oder Entwurf), Guthaben */
export interface PortalWkz {
  platzierungen: WkzPlatzierung[];
  werbemittel: (WerbemittelKurz & { status: WerbemittelStatus })[];
  /** Buchungen, bei denen der Werbetreibende am Zug ist */
  offen: number;
  guthaben: GuthabenStand;
  heute: string;
  fruehesterStart: string;
  /** false in der Portal-Ansicht des Marktplatzes oder bei gesperrtem Werbekonto */
  darfBuchen: boolean;
  hinweis: string;
}

/** Buchung im Belegungskalender des Kontos */
export interface WkzKalenderBuchung {
  id: string;
  werbekontoId: string;
  werbekonto: string;
  /** leer, solange keins zugeordnet ist */
  werbemittel: string;
  von: string;
  bis: string;
  status: WkzStatus;
  phase: WkzPhase;
  preisCent: number;
  abrechnung: WkzAbrechnung;
}

export interface WkzKalender {
  von: string;
  bis: string;
  heute: string;
  fruehesterStart: string;
  platzierungen: { platzierung: WkzPlatzierung; tage: WkzTag[]; buchungen: WkzKalenderBuchung[] }[];
}

/** Ergebnis einer Statusänderung mit Geldbewegung */
export interface WkzAktion {
  buchung: WkzBuchung;
  /** erstatteter Betrag (0 ohne Erstattung) */
  erstattetCent: number;
}
